/**
 * PATH: packages/bills/src/components/billsListKeyboardNav.ts
 * FILE_ID: BILLS-PKG-CMP-LIST-KBD-001
 * PURPOSE: Pure I→O helpers for roving-tabindex keyboard navigation of the
 *          bills left-rail listbox — map (option indices, current highlight,
 *          key) → the next highlighted option index.
 *
 * WHY: BillsListPanel renders a GROUPED render-item stream (statement headers
 *      interleaved with bill rows). Only the bill rows are selectable
 *      ``role="option"`` entries; group headers must be skipped during
 *      ArrowUp/ArrowDown/Home/End navigation. Lifting the index math out of the
 *      panel keeps it a small, table-testable function with no React/DOM deps
 *      so the windowing + focus wiring in the panel stays the only thing that
 *      needs a jsdom render to cover.
 *
 * FLOW:
 *   ┌───────────────────┐   ┌─────────────────────┐   ┌────────────────────┐
 *   │ optionIndices[] + │ → │ nextOptionIndex()   │ → │ next render-item   │
 *   │ current + NavKey  │   │ (this module)       │   │ index (or null)    │
 *   └───────────────────┘   └─────────────────────┘   └────────────────────┘
 *
 * DEPENDENCIES: none — pure module.
 *
 * RELATED:
 *   - BillsListPanel.tsx        (consumer; owns state + focus side effects)
 *   - billsListGrouping.ts      (produces the RenderItem stream we navigate)
 */

/** The navigation keys the listbox handles for moving the highlight. */
export type NavKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

/**
 * Given the render-item indices that are selectable options (bill rows —
 * group headers are excluded by the caller) and the currently highlighted
 * render-item index, return the next highlighted render-item index for a
 * navigation key.
 *
 * Conventions (WAI-ARIA listbox):
 *   - ``Home`` → first option, ``End`` → last option.
 *   - ``ArrowDown`` from nothing highlighted → first option; otherwise the
 *     next option, clamped at the last (no wrap — matches native selects and
 *     avoids a surprising jump to the top on a long list).
 *   - ``ArrowUp`` from nothing highlighted → last option; otherwise the
 *     previous option, clamped at the first.
 *   - Empty option list → ``null`` (nothing to highlight).
 *
 * pure: (number[], number | null, NavKey) → number | null
 */
export function nextOptionIndex(
  optionIndices: number[],
  current: number | null,
  key: NavKey,
): number | null {
  if (optionIndices.length === 0) return null;
  const first = optionIndices[0];
  const last = optionIndices[optionIndices.length - 1];
  const pos = current == null ? -1 : optionIndices.indexOf(current);

  switch (key) {
    case 'Home':
      return first;
    case 'End':
      return last;
    case 'ArrowDown':
      if (pos < 0) return first;
      return optionIndices[Math.min(pos + 1, optionIndices.length - 1)];
    case 'ArrowUp':
      if (pos < 0) return last;
      return optionIndices[Math.max(pos - 1, 0)];
    default:
      return current;
  }
}

/**
 * The render-item index the roving tabindex should live on when the user
 * hasn't explicitly navigated yet: the keyboard highlight if set, else the
 * currently selected row, else the first option (so Tab always lands on a
 * sensible entry point). Returns null only when there are no options.
 *
 * pure: (number | null, number | null, number[]) → number | null
 */
export function resolveHighlightIndex(
  activeIndex: number | null,
  selectedIndex: number | null,
  optionIndices: number[],
): number | null {
  if (activeIndex != null && optionIndices.includes(activeIndex)) {
    return activeIndex;
  }
  if (selectedIndex != null && optionIndices.includes(selectedIndex)) {
    return selectedIndex;
  }
  return optionIndices.length > 0 ? optionIndices[0] : null;
}

/** Narrow an arbitrary key string to a {@link NavKey}, or null. */
export function toNavKey(key: string): NavKey | null {
  return key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End'
    ? key
    : null;
}
